import React, { useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import NavbarStandard from 'components/pages/landing/NavbarStandard';
import FooterStandard from 'components/pages/landing/FooterStandard';
import { fetchPublicBlogPostBySlug } from 'helpers/blog';

/**
 * Parse FAQ items from a blog post.
 * Supports two modes:
 *   - 'json': faq_json is a JSON array of {question, answer}
 *   - 'manual': faq_manual uses "P: ...\nR: ..." blocks separated by blank lines
 */
const parseFaqItems = post => {
  if (!post) return [];
  if (post.faq_mode === 'json') {
    try {
      const items = JSON.parse(post.faq_json || '[]');
      return Array.isArray(items)
        ? items.filter(i => i && i.question && i.answer)
        : [];
    } catch {
      return [];
    }
  }
  if (post.faq_mode === 'manual') {
    const text = (post.faq_manual || '').trim();
    if (!text) return [];
    return text
      .split(/\n{2,}/)
      .map(block => {
        const qMatch = block.match(/^P:\s*(.+)/im);
        const aMatch = block.match(/^R:\s*([\s\S]+)/im);
        if (!qMatch || !aMatch) return null;
        return { question: qMatch[1].trim(), answer: aMatch[1].trim() };
      })
      .filter(Boolean);
  }
  return [];
};

const PublicBlogDetailPage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [post, setPost] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchPublicBlogPostBySlug(slug);
        setPost(response.post || null);
      } catch (requestError) {
        setPost(null);
        setError(requestError.response?.data?.error || t('BLOG.ERROR_LOAD'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug, t]);

  // Inject FAQPage JSON-LD for SEO
  useEffect(() => {
    if (!post) return;
    const faqItems = parseFaqItems(post);
    if (!faqItems.length) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blog-faq-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('blog-faq-jsonld');
      if (existing) existing.remove();
    };
  }, [post]);

  return (
    <>
      <NavbarStandard solid />
      <main>
        <section className="py-6 mt-6 bg-300">
          <Container>
            <Button as={Link} to="/blog" variant="link" className="ps-0 mb-2">
              {t('BLOG.BACK_TO_PUBLIC_LIST')}
            </Button>
            {loading ? (
              <div className="text-center py-6">
                <Spinner animation="border" />
              </div>
            ) : null}

            {!loading && error ? <Alert variant="danger">{error}</Alert> : null}

            {!loading && !error && post ? (
              <>
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {post.category ? <Badge bg="light" text="dark">{post.category}</Badge> : null}
                  {post.is_featured ? <Badge bg="warning" text="dark">{t('BLOG.STATUS_FEATURED')}</Badge> : null}
                </div>
                <h1 className="mb-2">{post.title}</h1>
                {post.subtitle ? <p className="text-700 fs-1 mb-3">{post.subtitle}</p> : null}
                <div className="text-500 fs--1">
                  {[post.author_name || post.author?.full_name || 'Gestodo', post.reading_time].filter(Boolean).join(' · ')}
                </div>
              </>
            ) : null}
          </Container>
        </section>

        {!loading && !error && post ? (
          <section className="py-5">
            <Container>
              <Card className="border">
                {post.cover_image_url ? (
                  <div className="ratio ratio-4x3">
                    <img
                      src={post.cover_image_url}
                      alt={post.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : null}
                <Card.Body className="p-4 p-lg-5">
                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: post.content || '' }}
                  />
                </Card.Body>
              </Card>

              {parseFaqItems(post).length > 0 ? (
                <div className="mt-5">
                  <h3 className="mb-4">{t('BLOG.FAQ_PUBLIC_TITLE')}</h3>
                  <Accordion>
                    {parseFaqItems(post).map((item, index) => (
                      <Accordion.Item eventKey={String(index)} key={index}>
                        <Accordion.Header>{item.question}</Accordion.Header>
                        <Accordion.Body className="text-700">{item.answer}</Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </div>
              ) : null}
            </Container>
          </section>
        ) : null}
      </main>
      <FooterStandard />
    </>
  );
};

export default PublicBlogDetailPage;
